import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Chip, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { ChevronDown, Plus } from "lucide-react";

import { AlertNote } from "@/components/AlertNote";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Checkbox } from "@/components/Checkbox";
import { KEYWORD_MAX_LENGTH, engineTitle, normalizeKeyword, splitLines } from "../seoSetupUtils";
import { useFlashNotice } from "../hooks";
import { EngineTable, NumberedTextarea, SUCCESS, ServerErrors, SetupCard } from "../components";

const counterColor = (count, limit) => {
  if (count > limit) return "blocked";
  if (count === limit) return "pending";
  return count > 0 ? "done" : "muted";
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export function KeywordsStep({ engines, keywords, onChange, limit, errors, showErrors, serverErrors }) {
  const [text, setText] = useState("");
  const [selected, setSelected] = useState(() => engines.map((engine) => engine.key));
  const [expanded, setExpanded] = useState(null);
  const [notice, setNotice] = useFlashNotice(3000);

  // Engines added after this step was first shown start selected; removed ones drop out.
  const engineKeys = engines.map((engine) => engine.key).join("|");
  useEffect(() => {
    setSelected((prev) => {
      const keys = engines.map((engine) => engine.key);
      const kept = prev.filter((key) => keys.includes(key));
      return kept.length ? kept : keys;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engineKeys]);

  const preview = useMemo(() => {
    const parsed = splitLines(text).map(normalizeKeyword).filter(Boolean);
    const tooLong = parsed.filter((keyword) => keyword.length > KEYWORD_MAX_LENGTH);
    const unique = [...new Set(parsed.filter((keyword) => keyword.length <= KEYWORD_MAX_LENGTH))];
    const targets = engines.filter((engine) => selected.includes(engine.key));
    const overflow = targets
      .map((engine) => {
        const existing = keywords[engine.key] || [];
        const added = unique.filter((keyword) => !existing.includes(keyword)).length;
        return { engine, existing: existing.length, added, total: existing.length + added };
      })
      .filter((row) => row.total > limit);
    return { unique, tooLong, targets, overflow };
  }, [text, engines, selected, keywords, limit]);

  const canAdd =
    preview.unique.length > 0 && preview.targets.length > 0 && !preview.overflow.length && !preview.tooLong.length;

  const handleAdd = () => {
    if (!canAdd) return;
    onChange((prev) => {
      const next = { ...prev.keywords };
      preview.targets.forEach((engine) => {
        const existing = next[engine.key] || [];
        next[engine.key] = [...existing, ...preview.unique.filter((keyword) => !existing.includes(keyword))];
      });
      return { keywords: next };
    });
    setNotice(
      `${plural(preview.unique.length, "keyword añadida", "keywords añadidas")} a ${plural(preview.targets.length, "motor", "motores")}.`
    );
    setText("");
  };

  const removeKeyword = (engineKey, keyword) =>
    onChange((prev) => ({
      keywords: { ...prev.keywords, [engineKey]: (prev.keywords[engineKey] || []).filter((k) => k !== keyword) },
    }));

  const toggle = (key) => setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  const selectedCount = engines.filter((engine) => selected.includes(engine.key)).length;
  const allSelected = engines.length > 0 && selectedCount === engines.length;
  const firstOverflow = preview.overflow[0];

  if (!engines.length) {
    return (
      <AlertNote variant="info">
        Solo has añadido motores de IA, que no usan keywords. Puedes continuar con el siguiente paso.
      </AlertNote>
    );
  }

  return (
    <Stack spacing={2}>
      <ServerErrors messages={serverErrors} />

      <SetupCard
        title="Elegir motores de búsqueda"
        description="Marca los motores en los que quieres añadir las keywords. Despliega un motor para ver o quitar las suyas."
      >
        <EngineTable
          engines={engines}
          errors={showErrors ? errors : undefined}
          header={{
            leading: (
              <Checkbox
                checked={allSelected}
                indeterminate={!allSelected && selectedCount > 0}
                onCheckedChange={(checked) => setSelected(checked ? engines.map((engine) => engine.key) : [])}
                inputProps={{ "aria-label": "Seleccionar todos los motores" }}
              />
            ),
            engine: `Motor de búsqueda (${selectedCount} de ${engines.length})`,
            trailing: "Keywords",
          }}
          trailingWidth={104}
          leading={(engine) => (
            <Checkbox
              checked={selected.includes(engine.key)}
              onCheckedChange={() => toggle(engine.key)}
              inputProps={{ "aria-label": `Seleccionar ${engineTitle(engine)}` }}
            />
          )}
          trailing={(engine) => {
            const count = keywords[engine.key]?.length || 0;
            const isOpen = expanded === engine.key;
            return (
              <>
                <Badge color={counterColor(count, limit)} dot={false} sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {count}/{limit}
                </Badge>
                <IconButton
                  size="small"
                  aria-label={isOpen ? "Ocultar keywords" : "Ver keywords"}
                  aria-expanded={isOpen}
                  onClick={() => setExpanded(isOpen ? null : engine.key)}
                  disabled={!count}
                >
                  <ChevronDown
                    size={18}
                    style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }}
                  />
                </IconButton>
              </>
            );
          }}
          below={(engine) => {
            const list = keywords[engine.key] || [];
            return (
              <Collapse in={expanded === engine.key && list.length > 0} unmountOnExit>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, px: 1.5, pb: 1.5, pl: { sm: 7.5 } }}>
                  {list.map((keyword) => (
                    <Chip
                      key={keyword}
                      label={keyword}
                      size="small"
                      onDelete={() => removeKeyword(engine.key, keyword)}
                      sx={{ borderRadius: "8px", backgroundColor: "#F1F5F9", maxWidth: "100%" }}
                    />
                  ))}
                </Box>
              </Collapse>
            );
          }}
        />
      </SetupCard>

      <SetupCard
        title="Ingresa las palabras clave que quieres añadir"
        description={`Escríbelas o pégalas, una por línea. Hasta ${limit} por motor.`}
        footer={
          <>
            {notice ? (
              <Typography variant="caption" sx={{ color: SUCCESS, fontWeight: 600 }} role="status">
                {notice}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {preview.unique.length > 0 ? plural(preview.unique.length, "keyword", "keywords") + " sin repetir" : " "}
              </Typography>
            )}
            <Button variant="dark" onClick={handleAdd} disabled={!canAdd} sx={{ width: { xs: "100%", sm: "auto" } }}>
              <Plus size={16} />
              Añadir a los motores seleccionados ({preview.targets.length})
            </Button>
          </>
        }
      >
        <NumberedTextarea
          id="seo-setup-keywords"
          value={text}
          onChange={setText}
          rows={10}
          placeholder={"dentista madrid\nclínica dental cerca de mí"}
          ariaLabel="Keywords, una por línea"
          error={preview.overflow.length > 0 || preview.tooLong.length > 0}
        />

        {firstOverflow && (
          <AlertNote variant="warning" sx={{ mt: 1.5 }}>
            {preview.overflow.length === 1
              ? `${engineTitle(firstOverflow.engine)} superaría el límite: ${firstOverflow.existing} + ${firstOverflow.added} = ${firstOverflow.total} de ${limit}.`
              : `${preview.overflow.length} motores superarían el límite de ${limit} keywords.`}{" "}
            Quita keywords o desmarca ese motor.
          </AlertNote>
        )}
        {preview.tooLong.length > 0 && (
          <AlertNote variant="warning" sx={{ mt: 1.5 }}>
            Hay {preview.tooLong.length === 1 ? "una keyword" : `${preview.tooLong.length} keywords`} de más de{" "}
            {KEYWORD_MAX_LENGTH} caracteres.
          </AlertNote>
        )}
      </SetupCard>
    </Stack>
  );
}

KeywordsStep.propTypes = {
  engines: PropTypes.arrayOf(PropTypes.object).isRequired,
  keywords: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  limit: PropTypes.number.isRequired,
  errors: PropTypes.object.isRequired,
  showErrors: PropTypes.bool,
  serverErrors: PropTypes.arrayOf(PropTypes.string),
};
